import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaOchoM(props, ref) {
  const { content = {}, logoSrc, bgColor } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 900;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // background
      ctx.fillStyle = opts.bgColor || bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // text defaults
      const title = props.title || opts.title || '';
      const subTitle = props.subTitle || opts.subTitle || '';

      // layout basics
      const padding = 28;
      let y = padding;
      const usableW = width - padding * 2;

      // Title square and title
      const titleFontSize = opts.titleFontSize || 40;
      const squareSize =
        typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 2);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 12;
      const squareText = opts.titleSquareText || '1.7';

      // draw square at left
      const squareX = padding;
      ctx.fillStyle = opts.titleSquareBg || '#0ea5e9';
      ctx.fillRect(squareX, y, squareSize, squareSize);

      // square number/text
      ctx.fillStyle = opts.titleSquareColor || '#ffffff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, squareX + squareSize / 2, y + squareSize / 2);

      // draw title to the right of square and subtitle inside the square height
      const titleX = squareX + squareSize + squareGap;
      if (title || subTitle) {
        const titleMaxW = Math.max(0, usableW - (squareSize + squareGap));
        const titleLines = title ? String(title).split('\n') : [];
        const subLines = subTitle ? String(subTitle).split('\n') : [];

        const baseTitleFontSize = titleFontSize;
        const baseSubSize = opts.subTitleFontSize || 40;
        const baseTitleLineH = Math.round(baseTitleFontSize * 1.2);
        const baseSubLineH = Math.round(baseSubSize);
        const titleSubtitleGap = typeof opts.titleSubtitleGap === 'number' ? opts.titleSubtitleGap : 12;

        let totalTextH =
          (titleLines.length ? titleLines.length * baseTitleLineH : 0) +
          (subLines.length ? titleSubtitleGap + subLines.length * baseSubLineH : 0);

        let titleFontToUse = baseTitleFontSize;
        let subFontToUse = baseSubSize;
        let titleLineH = baseTitleLineH;
        let subLineH = baseSubLineH;

        if (totalTextH > squareSize) {
          const scale = squareSize / totalTextH;
          titleFontToUse = Math.max(10, Math.floor(baseTitleFontSize * scale));
          subFontToUse = Math.max(8, Math.floor(baseSubSize * scale));
          titleLineH = Math.round(titleFontToUse * 1.2);
          subLineH = Math.max(10, Math.round(subFontToUse));
          totalTextH =
            (titleLines.length ? titleLines.length * titleLineH : 0) +
            (subLines.length ? titleSubtitleGap + subLines.length * subLineH : 0);
        }

        let textStartY = y + Math.round((squareSize - totalTextH) / 2);

        if (titleLines.length) {
          ctx.fillStyle = opts.titleColor || '#0f172a';
          ctx.font = `bold ${titleFontToUse}px Inter, Arial, sans-serif`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          let ty = textStartY;
          for (let ln of titleLines) {
            ctx.fillText(ln, titleX, ty);
            ty += titleLineH;
          }

          if (subLines.length) {
            let sy = textStartY + titleLines.length * titleLineH + titleSubtitleGap;
            ctx.fillStyle = opts.subTitleColor || '#374151';
            ctx.font = `${subFontToUse}px Inter, Arial, sans-serif`;
            ctx.textBaseline = 'top';
            for (let ln of subLines) {
              ctx.fillText(ln, titleX, sy);
              sy += subLineH;
            }
          }
        } else {
          // only subtitle
          if (subLines.length) {
            let sy = y + Math.round((squareSize - subLines.length * baseSubLineH) / 2);
            ctx.fillStyle = opts.subTitleColor || '#374151';
            ctx.font = `${baseSubSize}px Inter, Arial, sans-serif`;
            ctx.textBaseline = 'top';
            for (let ln of subLines) {
              ctx.fillText(ln, titleX, sy);
              sy += baseSubLineH;
            }
          }
        }

        // advance below the square
        y = y + squareSize;
      } else {
        y = y + squareSize;
      }

      // fixed gap after title/square before placing graphics
      y += typeof opts.chartsGapAfterTitle === 'number' ? opts.chartsGapAfterTitle : 200;

      // compute charts area (a single centered pie)
      const pageChartsBottom = height - padding - 150; // room for logo/captions
      const maxAllowedBottom = typeof opts.maxChartsBottom === 'number' ? opts.maxChartsBottom : 800;
      const chartsBottom = Math.min(pageChartsBottom, maxAllowedBottom);
      const chartsTop = y;
      const chartsH = Math.max(80, chartsBottom - chartsTop);
      const chartsW = usableW;

      const palette = opts.palette || ['#4488ef', '#5ed8ee', '#360bf5', '#103db9', '#6366f1', '#532ace'];

      // helper: contrast guess
      const contrastFor = (hex) => {
        try {
          const h = String(hex || '').replace('#', '');
          const r = parseInt(h.length === 3 ? h[0] + h[0] : h.slice(0, 2), 16);
          const g = parseInt(h.length === 3 ? h[1] + h[1] : h.slice(2, 4), 16);
          const b = parseInt(h.length === 3 ? h[2] + h[2] : h.slice(4, 6), 16);
          const lum = 0.299 * r + 0.587 * g + 0.114 * b;
          return lum < 140 ? '#ffffff' : '#0f172a';
        } catch (e) {
          return '#ffffff';
        }
      };

      // helper: wrap text into lines fitting maxW
      const wrapLines = (c, text, maxW) => {
        const words = String(text || '').split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = '';
        for (let w of words) {
          const test = cur ? cur + ' ' + w : w;
          if (c.measureText(test).width <= maxW) {
            cur = test;
          } else {
            if (cur) lines.push(cur);
            cur = w;
          }
        }
        if (cur) lines.push(cur);
        return lines;
      };

      // helper: draw justified line (distribute spaces evenly)
      const drawJustifiedLine = (c, text, x, y, maxWidth) => {
        const words = String(text || '').split(' ');
        if (words.length <= 1) {
          c.fillText(text, x, y);
          return;
        }
        let wordsWidth = 0;
        words.forEach((w) => {
          wordsWidth += c.measureText(w).width;
        });
        const extraSpace = Math.max(0, maxWidth - wordsWidth);
        const gap = extraSpace / (words.length - 1);
        let xPos = x;
        words.forEach((w, idx) => {
          c.fillText(w, xPos, y);
          xPos += c.measureText(w).width + (idx < words.length - 1 ? gap : 0);
        });
      };

      // helper: draw multiple justified lines
      const drawJustifiedLines = (c, lines, x, y0, maxWidth, lh) => {
        let yPos = y0;
        lines.forEach((line, idx) => {
          const isLast = idx === lines.length - 1;
          if (isLast) {
            c.fillText(line, x, yPos);
          } else {
            drawJustifiedLine(c, line, x, yPos, maxWidth);
          }
          yPos += lh;
        });
        return yPos;
      };

      // draw vertical bars chart
      const drawBars = (cxLeft, cyTop, maxW, maxH, items, caption) => {
        const innerPad = 12;
        const capSize = opts.captionSize || 25;
        const capLineH = Math.round(capSize * 1.2);
        const capLines = caption ? wrapLines(ctx, caption, maxW - innerPad * 1) : [];

        const legendRowH = Math.round(opts.legendFontSize || 12) + 4;
        const reservedBottom = innerPad * 2 + legendRowH + (capLines.length * capLineH) + 12;

        const contentH = Math.max(40, maxH - reservedBottom);
        const contentW = Math.max(40, maxW - innerPad * 2);

        // compute max value
        const maxVal = items.reduce((m, it) => Math.max(m, Number(it.total) || 0), 0) || 1;
        
        // bar sizing
        const n = Math.max(1, items.length);
        const barGap = typeof opts.barGap === 'number' ? opts.barGap : 20;
        const totalGap = (n - 1) * barGap;
        const slotW = Math.max(8, Math.floor((contentW - totalGap) / n));
        const barMaxH = contentH;
        
        const chartX = cxLeft + innerPad;
        const chartY = cyTop;
        const chartBottom = chartY + barMaxH;

        // draw bars
        ctx.font = `${opts.barLabelSize || 20}px Inter, Arial, sans-serif`;
        ctx.textBaseline = 'top';
        
        for (let i = 0; i < n; i++) {
          const it = items[i] || {};
          const v = Number(it.total) || 0;
          const h = Math.max(0, Math.round((v / maxVal) * barMaxH * 0.85));
          
          const barX = chartX + i * (slotW + barGap);
          const barY = chartBottom - h;
          
          const color = palette[i % palette.length];
          ctx.fillStyle = color;
          ctx.fillRect(barX, barY, slotW, h);

          // value label on top of bar
          ctx.fillStyle = opts.barValueColor || '#0f172a';
          ctx.font = `bold ${Math.max(10, opts.barValueFontSize || 15)}px Inter, Arial, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          if (h > 20) {
            ctx.fillText(String(v), barX + Math.round(slotW / 2), barY - 4);
          } else {
            ctx.fillText(String(v), barX + Math.round(slotW / 2), barY - 6);
          }

          // category label under bar
          ctx.font = `${opts.barLabelSize || 20}px Inter, Arial, sans-serif`;
          ctx.fillStyle = opts.barLabelColor || '#334155';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          const label = String(it.categoria || '');
          let displayLabel = label;
          while (ctx.measureText(displayLabel).width > slotW && displayLabel.length > 0) {
            displayLabel = displayLabel.slice(0, -1);
          }
          if (displayLabel.length < label.length) displayLabel = displayLabel.slice(0, Math.max(0, displayLabel.length - 1)) + '...';
          ctx.fillText(displayLabel, barX + Math.round(slotW / 2), chartBottom + 6);

          // departamento label above bar
          const deptLabel = String(it.departamento || '');
          if (deptLabel) {
            ctx.font = `${Math.max(8, opts.deptLabelSize || 15)}px Inter, Arial, sans-serif`;
            ctx.fillStyle = opts.deptLabelColor || '#475569';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            let displayDept = deptLabel;
            while (ctx.measureText(displayDept).width > slotW && displayDept.length > 0) {
              displayDept = displayDept.slice(0, -1);
            }
            if (displayDept.length < deptLabel.length) displayDept = displayDept.slice(0, Math.max(0, displayDept.length - 1)) + '...';
            ctx.fillText(displayDept, barX + Math.round(slotW / 2), chartY - 4);
          }
        }

        // legend (single row with all items)
        const legendY = chartBottom + 40;
        ctx.font = `${opts.legendFontSize || 20}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';

        const legendStartX = cxLeft + innerPad;
        const legendAvailW = Math.max(0, maxW - innerPad * 2);
        const legendLabels = Array.from({ length: n }, (_, i) => {
          const it = items[i] || {};
          return `${it.categoria || ''} (${it.total || 0})`;
        });
        const legendTotalW = legendLabels.reduce((sum, label, i) => {
          return sum + 18 + ctx.measureText(label).width + (i < n - 1 ? 16 : 0);
        }, 0);
        let legendX = legendStartX + Math.max(0, Math.round((legendAvailW - legendTotalW) / 2));
        for (let i = 0; i < n; i++) {
          const color = palette[i % palette.length];
          const legendLabel = legendLabels[i] || '';
          ctx.fillStyle = color;
          ctx.fillRect(legendX, legendY, 12, 12);
          ctx.fillStyle = opts.legendColor || '#111827';
          ctx.fillText(legendLabel, legendX + 18, legendY);

          const labelW = ctx.measureText(legendLabel).width;
          legendX += 18 + labelW + (i < n - 1 ? 16 : 0);
        }

        // caption under legend (centered)
        if (capLines.length) {
          ctx.fillStyle = opts.captionColor || '#374151';
          ctx.font = `${capSize}px Inter, Arial, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          const capX = cxLeft + innerPad;
          const capW = Math.max(16, maxW - innerPad * 2);
          let capY = legendY + 50;
          for (let i = 0; i < capLines.length; i++) {
            const ln = capLines[i];
            const isLast = i === capLines.length - 1;
            if (isLast) {
              ctx.fillText(ln, capX, capY);
            } else {
              drawJustifiedLine(ctx, ln, capX, capY, capW);
            }
            capY += capLineH;
          }
        }
      };

      // split layout: bars on left, qualitative text on right
      const qual = Array.isArray(props.textoCualitativo)
        ? props.textoCualitativo
        : props.textoCualitativo
        ? [props.textoCualitativo]
        : [];
      
      const hasQual = qual.length > 0;
      const colGap = 24;
      const barsColW = hasQual ? Math.round(usableW * 0.55) : usableW;
      const qualColW = hasQual ? Math.round(usableW * 0.45) - colGap : 0;
      const barsColX = padding;
      const qualColX = barsColX + barsColW + colGap;

      // draw the bars using graficaUnoData on left
      const graf1 = props.graficaUnoData || [];
      const cap1 = props.textoGraficoUno || opts.textoGraficoUno || '';
      drawBars(barsColX, chartsTop, barsColW, chartsH, graf1, cap1);

      // draw qualitative text on right
      if (hasQual) {
        let qualX = qualColX;
        let qualY = chartsTop;
        const qualFontSize = opts.qualFontSize || 30;
        ctx.font = `${qualFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.qualColor || '#374151';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        const lineHeight = Math.round(qualFontSize * 1.25);

        const dash = '- ';
        const dashW = ctx.measureText(dash).width;

        for (let item of qual) {
          const text = String(item || '');
          const availableW = Math.max(16, qualColW - dashW);
          let lines = wrapLines(ctx, text, availableW);
          if (!lines || lines.length === 0) lines = [''];

          // draw first line with dash prefix, justified
          ctx.fillText(dash, qualX, qualY);
          const isFirstLast = lines.length === 1;
          if (isFirstLast) {
            ctx.fillText(lines[0] || '', qualX + dashW, qualY);
          } else {
            drawJustifiedLine(ctx, lines[0] || '', qualX + dashW, qualY, availableW);
          }
          qualY += lineHeight;

          // draw subsequent wrapped lines justified to fill the width
          for (let i = 1; i < lines.length; i++) {
            const isLast = i === lines.length - 1;
            if (isLast) {
              ctx.fillText(lines[i], qualX + dashW, qualY);
            } else {
              drawJustifiedLine(ctx, lines[i], qualX + dashW, qualY, availableW);
            }
            qualY += lineHeight;
          }

          // small gap between items
          qualY += 8;
        }
      }

      // logo bottom-right (draw then resolve)
      const mainLogoSrc = opts.logoSrc || props.logoSrc || logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const maxW = Math.min(240, Math.round(width * 0.12));
            const w = Math.min(maxW, img.width);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = height - padding - h;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) {
            /* ignore drawing errors */
          }
          resolve({ canvas, width, height });
        };
        img.onerror = () => resolve({ canvas, width, height });
        img.src = mainLogoSrc;
      } else {
        resolve({ canvas, width, height });
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_ocho_${new Date().toISOString().slice(0, 10)}.png`;
    a.click();
  };

  useImperativeHandle(
    ref,
    () => ({
      draw,
      download,
      getDataUrl: () => {
        const canvas = canvasRef.current;
        return canvas ? canvas.toDataURL('image/png') : null;
      },
    }),
    [draw]
  );

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});